import { Box, Stack } from '@mui/joy';
import React from 'react';
import { Outlet } from 'react-router-dom';

import { PatientEditDialog } from '@MP/features/patient';
import { Patient } from '@MP/shared/api/hooks/patient';
import { selectIsTempUser, selectUser, selectUserAgreements, usePatientStore } from '@MP/shared/providers/PatientProvider';
import { useGlobalUIStore } from '@MP/shared/stores/GlobalUIStore';

import PatientDeleteConfirmDialog from './PatientDeleteConfirmDialog';
import PatientListContents from './PatientListContents';
import PatientMenuToolBar from './PatientMenuToolBar';
import TempPatientContents from './TempPatientContents';

const commonStyle = { p: '8px', flexGrow: 1, bgcolor: '#F5F5F5', minWidth: '1300px' };

const expandedStyle = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  bgcolor: '#F5F5F5',
  zIndex: 2,
};
const PatientPage = () => {
  const expanded = useGlobalUIStore((s) => s.expanded);
  const user = usePatientStore(selectUser);
  const userAgreements = usePatientStore(selectUserAgreements);
  const isTempUser = usePatientStore(selectIsTempUser);
  const openEditDialog = usePatientStore((s) => s.openEditDialog);
  const openDeleteDialog = usePatientStore((s) => s.openDeleteDialog);
  const deleteUser = usePatientStore((s) => s.deleteUser);
  const setOpenDeleteDialog = usePatientStore((s) => s.setOpenDeleteDialog);
  const setOpenEditDialog = usePatientStore((s) => s.setOpenEditDialog);

  // 삭제 대상 환자를 다이얼로그 열 때 스냅샷으로 고정한다. store.user 를 그대로 참조하면
  // 다른 PC 의 삭제(PATIENT_DELETED) 로 selection 이 다음 환자로 넘어갈 때 확인창이 조용히
  // 그 환자를 가리켜, 확인 시 엉뚱한 환자가 삭제될 수 있다(복구 불가).
  const [deleteTarget, setDeleteTarget] = React.useState<Patient | null>(null);

  const handleOpenDelete = () => {
    setDeleteTarget(user ?? null);
    setOpenDeleteDialog(true);
    setOpenEditDialog(false);
  };

  const handleDelete = (user: Patient, result: boolean) => {
    if (result) {
      deleteUser(user.id);
    }
    setOpenDeleteDialog(false);
    setDeleteTarget(null);
  };

  return (
    <Stack direction={'row'} sx={{ height: '100%' }}>
      <PatientListContents />
      <Box height={'100%'} sx={expanded ? expandedStyle : commonStyle}>
        {user && isTempUser && <TempPatientContents tempUser={user} />}
        {!(user && isTempUser) && (
          <Stack sx={{ overflow: 'auto', height: '100%', position: 'relative' }} rowGap={'8px'}>
            <PatientMenuToolBar />
            <Stack
              flexGrow={1}
              sx={{
                borderRadius: expanded ? 0 : '8px',
                bgcolor: 'common.white',
                overflow: 'auto',
                minWidth: expanded ? '1300px' : undefined,
              }}>
              <Outlet />
            </Stack>
          </Stack>
        )}
      </Box>
      {user && openEditDialog && (
        <PatientEditDialog
          value={user}
          agreements={userAgreements}
          onClose={() => setOpenEditDialog(false)}
          onDelete={handleOpenDelete}
        />
      )}
      {deleteTarget && openDeleteDialog && (
        <PatientDeleteConfirmDialog user={deleteTarget} onResult={handleDelete} />
      )}
    </Stack>
  );
};

export default PatientPage;
